import React from 'react';
import { Box, alpha, useTheme } from '@mui/material';

export type PillTabsOption<T extends string> = {
  id: T;
  label: string;
  chip?: string | null;
};

type Props<T extends string> = {
  options: PillTabsOption<T>[];
  value: T;
  onChange: (id: T) => void;
  ariaLabel?: string;
};

// Sliding-pill tab control. Used by BillingCycleTabs and PaymentStep's
// Card/Crypto switch so they share visual language.
export function PillTabs<T extends string>({ options, value, onChange, ariaLabel }: Props<T>) {
  const theme = useTheme();
  const idx = Math.max(
    0,
    options.findIndex((o) => o.id === value)
  );
  const count = options.length;
  const hasChip = options.some((o) => o.chip);

  return (
    <Box
      role="tablist"
      aria-label={ariaLabel}
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
        bgcolor: alpha(theme.palette.text.primary, 0.04),
        border: `1px solid ${alpha(theme.palette.text.primary, 0.05)}`,
        borderRadius: 999,
        p: '4px',
        mt: hasChip ? 1.5 : 0
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          width: `calc((100% - 8px) / ${count})`,
          left: `calc(4px + ${idx} * (100% - 8px) / ${count})`,
          borderRadius: 999,
          background: `linear-gradient(180deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.03)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
          boxShadow: `0 1px 2px rgba(11,15,32,.06), 0 6px 18px -8px ${alpha(theme.palette.primary.main, 0.35)}`,
          transition: 'left 350ms cubic-bezier(.4,0,.2,1)'
        }}
      />
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <Box
            key={opt.id}
            component="button"
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.id)}
            sx={{
              position: 'relative',
              zIndex: 1,
              appearance: 'none',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              height: 42,
              px: 1,
              borderRadius: 999,
              fontSize: 13,
              fontFamily: 'inherit',
              fontWeight: active ? 700 : 500,
              letterSpacing: active ? 0.2 : 0,
              color: active ? theme.palette.primary.main : theme.palette.text.secondary,
              transition: 'color 200ms ease, background 200ms ease, letter-spacing 200ms ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': active
                ? {}
                : {
                    color: theme.palette.text.primary,
                    background: alpha(theme.palette.text.primary, 0.04)
                  }
            }}
          >
            <span>{opt.label}</span>
            {opt.chip && (
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: -11,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap',
                  fontSize: 9,
                  lineHeight: 1,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  px: 1.1,
                  py: '4px',
                  borderRadius: 999,
                  color: '#fff',
                  background: 'linear-gradient(135deg, #ff9b4d 0%, #ff5b3d 100%)',
                  boxShadow: '0 4px 10px -4px rgba(255,91,61,.55), inset 0 1px 0 rgba(255,255,255,.25)',
                  transition: 'transform 180ms ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    bottom: -3,
                    left: '50%',
                    transform: 'translateX(-50%) rotate(45deg)',
                    width: 6,
                    height: 6,
                    background: 'linear-gradient(135deg, #ff7e44, #ff5b3d)',
                    borderRadius: '1px'
                  }
                }}
              >
                {opt.chip}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
